import React from 'react';
import { ReinforcementStudent, ReinforcementStudentTopic } from '../../types';

/**
 * Subject-wise health rows — one row per subject/curriculum with a stacked
 * got/partly/not-yet mini-bar. Shared by the Home class card, the class
 * report, and the student report so "by subject" reads the same everywhere.
 */

export interface SubjectHealthGroup {
  label: string;
  got: number;
  partly: number;
  notyet: number;
  /** Topics/students the advisor hasn't assessed yet — neutral, not bad. */
  explored: number;
}

// NCERT subjects lead; Global sub-subjects (Physics, Biology, …) sort after
// them alphabetically; the generic 'Global'/'Other' buckets come last.
const NCERT_ORDER = ['Maths', 'Science', 'Social'];

export const subjectLabel = (subject?: string | null): string => {
  const s = (subject ?? '').trim();
  if (!s) return 'Other';
  const lower = s.toLowerCase();
  if (lower === 'maths') return 'Maths';
  if (lower === 'science') return 'Science';
  if (lower === 'social') return 'Social';
  if (lower === 'global') return 'STEM';
  // A Global sub-subject (e.g. "physics") — show it as given, title-cased.
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const subjectRank = (label: string): number => {
  const ncert = NCERT_ORDER.indexOf(label);
  if (ncert !== -1) return ncert; // 0..2
  if (label === 'Other') return 1000;
  if (label === 'STEM') return 999;
  return 100; // named Global sub-subjects, ordered alphabetically within this band
};

/** Distinct subject labels present across any records carrying a `subject`,
 *  ordered NCERT-first then STEM sub-subjects (matches SubjectHealthRows). */
export const distinctSubjectLabels = (
  records: Array<{ subject?: string | null }>
): string[] => {
  const labels = new Set<string>();
  records.forEach((r) => labels.add(subjectLabel(r.subject)));
  return Array.from(labels).sort((a, b) => {
    const r = subjectRank(a) - subjectRank(b);
    return r !== 0 ? r : a.localeCompare(b);
  });
};

const sortGroups = (groups: SubjectHealthGroup[]): SubjectHealthGroup[] =>
  [...groups].sort((a, b) => {
    const r = subjectRank(a.label) - subjectRank(b.label);
    return r !== 0 ? r : a.label.localeCompare(b.label);
  });

export type HealthBucket = 'got' | 'partly' | 'notyet' | 'explored';

/** One topic-state → bucket. 'unknown' (advisor hasn't assessed) is neutral
 *  "explored", never good or bad. */
export const stateBucket = (state: string): HealthBucket => {
  if (state === 'solid') return 'got';
  if (state === 'partly_solid') return 'partly';
  if (state === 'forgotten' || state === 'never_learned') return 'notyet';
  return 'explored';
};

/** A student's rolled-up bucket across a set of topics: any not-yet dominates,
 *  then partly, then got; only-explored stays explored. */
export const rollupBucket = (states: string[]): HealthBucket => {
  const buckets = states.map(stateBucket);
  if (buckets.includes('notyet')) return 'notyet';
  if (buckets.includes('partly')) return 'partly';
  if (buckets.includes('got')) return 'got';
  return 'explored';
};

const emptyGroup = (label: string): SubjectHealthGroup => ({
  label,
  got: 0,
  partly: 0,
  notyet: 0,
  explored: 0,
});

/** Student report: count this student's TOPICS per state, per subject. */
export const topicsBySubject = (topics: ReinforcementStudentTopic[]): SubjectHealthGroup[] => {
  const map = new Map<string, SubjectHealthGroup>();
  topics.forEach((t) => {
    const label = subjectLabel(t.subject);
    const g = map.get(label) ?? emptyGroup(label);
    g[stateBucket(t.state)] += 1;
    map.set(label, g);
  });
  return sortGroups(Array.from(map.values()));
};

/** Which curriculum a report subject belongs to. NCERT subjects are the three
 *  standard ones; everything else (the teacher's STEM syllabus, whether tagged
 *  'global' or a named sub-subject like physics) is STEM; untagged is Other. */
export type CurriculumKey = 'NCERT' | 'STEM' | 'Other';

const NCERT_SUBJECTS = new Set(['maths', 'science', 'social']);

export const curriculumForSubject = (subject?: string | null): CurriculumKey => {
  const s = (subject ?? '').trim().toLowerCase();
  if (!s) return 'Other';
  if (NCERT_SUBJECTS.has(s)) return 'NCERT';
  return 'STEM';
};

export interface CurriculumHealth extends SubjectHealthGroup {
  curriculum: CurriculumKey;
  /** Per-subject rows within this curriculum. */
  groups: SubjectHealthGroup[];
}

/** Student report: split topics into curriculum buckets (NCERT / STEM / Other),
 *  and within each bucket group by subject. Each bucket also carries a rollup of
 *  its own topic counts so the caller can show a curriculum-level summary. */
export const topicsByCurriculum = (topics: ReinforcementStudentTopic[]): CurriculumHealth[] => {
  const byCurr = new Map<CurriculumKey, ReinforcementStudentTopic[]>();
  topics.forEach((t) => {
    const c = curriculumForSubject(t.subject);
    byCurr.set(c, [...(byCurr.get(c) ?? []), t]);
  });
  const order: CurriculumKey[] = ['NCERT', 'STEM', 'Other'];
  return order
    .filter((c) => byCurr.has(c))
    .map((c) => {
      const list = byCurr.get(c)!;
      const groups = topicsBySubject(list);
      const roll = groups.reduce(
        (acc, g) => ({
          got: acc.got + g.got,
          partly: acc.partly + g.partly,
          notyet: acc.notyet + g.notyet,
          explored: acc.explored + g.explored,
        }),
        { got: 0, partly: 0, notyet: 0, explored: 0 }
      );
      return { curriculum: c, label: c, groups, ...roll };
    });
};

/** Class surfaces: count STUDENTS per rolled-up state, per subject — a student
 *  counts once per subject they've touched. */
export const classHealthBySubject = (students: ReinforcementStudent[]): SubjectHealthGroup[] => {
  const map = new Map<string, SubjectHealthGroup>();
  students.forEach((s) => {
    const statesBySubject = new Map<string, string[]>();
    (s.topics || []).forEach((t) => {
      const label = subjectLabel(t.subject);
      statesBySubject.set(label, [...(statesBySubject.get(label) ?? []), t.state]);
    });
    statesBySubject.forEach((states, label) => {
      const g = map.get(label) ?? emptyGroup(label);
      g[rollupBucket(states)] += 1;
      map.set(label, g);
    });
  });
  return sortGroups(Array.from(map.values()));
};

const SubjectHealthRows: React.FC<{ groups: SubjectHealthGroup[] }> = ({ groups }) => {
  if (groups.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {groups.map((g) => {
        const total = g.got + g.partly + g.notyet + g.explored;
        if (total === 0) return null;
        return (
          <div key={g.label} className="flex items-center gap-3">
            <span className="w-16 flex-none text-xs font-semibold text-slate-600">{g.label}</span>
            <div className="flex h-5 flex-1 overflow-hidden rounded-md border border-[#E9E6F4]">
              {g.got > 0 && (
                <span
                  className="flex items-center justify-center bg-emerald-500 text-[10px] font-bold text-white"
                  style={{ width: `${(g.got / total) * 100}%` }}
                >
                  {g.got}
                </span>
              )}
              {g.partly > 0 && (
                <span
                  className="flex items-center justify-center bg-amber-500 text-[10px] font-bold text-white"
                  style={{ width: `${(g.partly / total) * 100}%` }}
                >
                  {g.partly}
                </span>
              )}
              {g.notyet > 0 && (
                <span
                  className="flex items-center justify-center bg-rose-500 text-[10px] font-bold text-white"
                  style={{ width: `${(g.notyet / total) * 100}%` }}
                >
                  {g.notyet}
                </span>
              )}
              {g.explored > 0 && (
                <span
                  className="flex items-center justify-center bg-slate-300 text-[10px] font-bold text-slate-600"
                  style={{ width: `${(g.explored / total) * 100}%` }}
                  title="Explored — the curiosity hasn't assessed these yet"
                >
                  {g.explored}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SubjectHealthRows;
